import { Link, usePage } from '@inertiajs/react';
import { MapPinned, ShieldCheck, Sparkles, TimerReset } from 'lucide-react';
import AppLogoIcon from '@/components/app-logo-icon';
import type { AuthLayoutProps } from '@/types';

export default function AuthSimpleLayout({
    children,
    title,
    description,
}: AuthLayoutProps) {
    const { config } = usePage().props as any;
    const name = config?.nama_instansi || 'Presence';
    const logo = config?.site_logo;

    return (
        <div className="relative flex min-h-svh items-center justify-center overflow-hidden p-4 md:p-10">
            {/* Background: Premium Mesh Gradient */}
            <div className="absolute inset-0 -z-10 bg-[#f8faf7] dark:bg-[#0c110d]" />
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_0%_0%,#006944_0%,transparent_50%),radial-gradient(circle_at_100%_0%,#ffd709_0%,transparent_50%),radial-gradient(circle_at_100%_100%,#006944_0%,transparent_50%),radial-gradient(circle_at_0%_100%,#ffd709_0%,transparent_50%)] opacity-30 blur-[100px] dark:opacity-20" />

            <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[1fr_1.1fr]">
                {/* Left Side: Features Card */}
                <div className="hidden min-h-[40rem] flex-col justify-between rounded-[2.5rem] bg-gradient-to-br from-[#006944] to-[#004d32] p-10 text-white shadow-[0_32px_96px_rgba(0,105,68,0.2)] ring-1 ring-white/20 lg:flex">
                    <div className="flex items-center justify-between gap-4">
                        <Link href="/" className="flex items-center gap-3">
                            <div className="flex size-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-xl shadow-black/20">
                                {logo ? (
                                    <img
                                        src={logo}
                                        alt={name}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <AppLogoIcon className="size-7 fill-[#006944]" />
                                )}
                            </div>
                            <div className="leading-tight">
                                <div className="text-2xl font-black tracking-tighter uppercase">
                                    {name}
                                </div>
                                <div className="text-sm text-white/70">
                                    Presensi Santri Digital
                                </div>
                            </div>
                        </Link>
                        <div className="rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-wider text-white uppercase ring-1 ring-white/20 backdrop-blur-sm">
                            {name}
                        </div>
                    </div>

                    <div className="max-w-xl space-y-6 py-12">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#ffd709]/30 bg-[#ffd709]/20 px-4 py-1.5 text-xs font-bold text-[#ffd709]">
                            <Sparkles className="size-4" />
                            VERSI BETA
                        </div>
                        <div className="space-y-4">
                            <h1 className="text-4xl leading-[1.1] font-extrabold tracking-tight md:text-6xl">
                                {title}
                            </h1>
                            <p className="max-w-md text-lg leading-relaxed text-white/80">
                                {description}
                            </p>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="group rounded-3xl bg-white/5 p-5 ring-1 ring-white/10 transition-all hover:bg-white/10">
                            <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-[#ffd709] shadow-[0_8px_16px_rgba(255,215,9,0.2)]">
                                <MapPinned className="size-5 text-[#006944]" />
                            </div>
                            <div className="text-sm font-bold">LOKASI GPS</div>
                            <p className="mt-1 text-xs leading-normal text-white/60">
                                Verifikasi kehadiran presisi tinggi.
                            </p>
                        </div>
                        <div className="group rounded-3xl bg-white/5 p-5 ring-1 ring-white/10 transition-all hover:bg-white/10">
                            <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-[#ffd709] shadow-[0_8px_16px_rgba(255,215,9,0.2)]">
                                <TimerReset className="size-5 text-[#006944]" />
                            </div>
                            <div className="text-sm font-bold">REAL-TIME</div>
                            <p className="mt-1 text-xs leading-normal text-white/60">
                                Pencatatan waktu instan & akurat.
                            </p>
                        </div>
                        <div className="group rounded-3xl bg-white/5 p-5 ring-1 ring-white/10 transition-all hover:bg-white/10">
                            <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-[#ffd709] shadow-[0_8px_16px_rgba(255,215,9,0.2)]">
                                <ShieldCheck className="size-5 text-[#006944]" />
                            </div>
                            <div className="text-sm font-bold">TERENKRIPSI</div>
                            <p className="mt-1 text-xs leading-normal text-white/60">
                                Data kehadiran aman & terjamin.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Side: Form Card */}
                <div className="flex items-center">
                    <div className="w-full rounded-[2.5rem] bg-white/95 p-8 shadow-[0_32px_80px_rgba(0,0,0,0.08)] ring-1 ring-black/5 backdrop-blur-xl md:p-12 dark:bg-[#1a1f1b]/95 dark:ring-white/5">
                        <div className="mb-8 space-y-2 border-b border-border/60 pb-6">
                            <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase">
                                Akses Autentikasi
                            </p>
                            <h2 className="text-3xl font-black tracking-tight text-foreground">
                                {title}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                Silakan masukkan detail kredensial Anda untuk
                                melanjutkan.
                            </p>
                        </div>

                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
